import React, { useMemo } from 'react';

import { MergeFieldRegistryEntry } from '~forms/types';

import { renderRichText } from './renderRichText';
import { isRichTextEmpty } from './sanitizeRichText';

interface ComponentProps {
  html: string;
  // Merge-field registry — lets pasted {{merge:<token>}} sentinels render as the shared blue chip.
  registry?: MergeFieldRegistryEntry[];
  className?: string;
  ariaLabel?: string;
  // Text shown when there is no content (canvas/editor want a prompt; the filler shows nothing).
  emptyText?: string;
}

/**
 * Read-only render of rich-text content: sanitises the stored HTML and displays it, turning inline
 * merge-field sentinels into chips. This is the "view only, same formatting" surface the filler sees
 * (e.g. T&Cs).
 */
const RichTextView: React.FC<ComponentProps> = ({ html, registry, className, ariaLabel, emptyText }) => {
  const content = useMemo(() => renderRichText(html, registry), [html, registry]);
  const empty = useMemo(() => isRichTextEmpty(html), [html]);

  const classes = ['rich-text-view', className].filter(Boolean).join(' ');

  if (empty) {
    if (!emptyText) return null;
    return (
      <div className={`${classes} rich-text-view--empty`} aria-label={ariaLabel}>
        <span className="text-muted">{emptyText}</span>
      </div>
    );
  }

  return (
    <div className={classes} aria-label={ariaLabel}>
      {content}
    </div>
  );
};

export default RichTextView;
